import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import BrandLogo from '@/components/BrandLogo';

export default function Navbar() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { label: t('nav_home'), href: '#hero' },
    { label: t('nav_features_amenities'), href: '#amenities' },
    { label: t('villas_title'), href: '#villas' },
    { label: t('map_subtitle'), href: '#location' },
    { label: t('rules_title'), href: '#rules' },
  ];

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50 border-b border-background-200/60">
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          <BrandLogo
            href="#hero"
            onClick={(e) => handleNavClick(e as unknown as React.MouseEvent<HTMLAnchorElement>, '#hero')}
          />

          <div className="hidden lg:flex items-center gap-5">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-sm font-medium tracking-wide whitespace-nowrap transition-colors duration-300 hover:text-primary-500 text-foreground-700"
              >
                {link.label}
              </a>
            ))}
            <LanguageSwitcher />
            <a
              href="#search"
              onClick={(e) => handleNavClick(e, '#search')}
              className="bg-primary-500 text-white px-5 py-2.5 rounded-md text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors duration-300 cursor-pointer"
            >
              {t('nav_book_now')}
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors duration-300 cursor-pointer text-foreground-950"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      <div
        className={`lg:hidden fixed inset-0 top-16 md:top-20 bg-background-50 z-40 transform transition-transform duration-400 ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col items-center justify-center h-full gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-foreground-950 text-lg font-medium font-heading tracking-wide"
            >
              {link.label}
            </a>
          ))}
          <LanguageSwitcher />
          <a
            href="#search"
            onClick={(e) => handleNavClick(e, '#search')}
            className="bg-primary-500 text-white px-8 py-3 rounded-md text-base font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors duration-300 cursor-pointer"
          >
            {t('nav_book_now')}
          </a>
        </div>
      </div>
    </nav>
  );
}